import { useState, useEffect, useCallback } from 'react';
import { createReview, hasUserReviewed, updateDocument } from '../firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook to manage reviews for a raffle
 *
 * @param {string} raffleId - The raffle ID
 * @param {string} revieweeId - The user being reviewed
 * @returns {object} - Review state and actions
 */
export function useReview(raffleId, revieweeId) {
  const { user } = useAuth();
  const [hasReviewed, setHasReviewed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Check if user has already reviewed
  useEffect(() => {
    async function checkReview() {
      if (!raffleId || !user?.uid || !revieweeId) {
        setLoading(false);
        return;
      }

      try {
        const reviewed = await hasUserReviewed(raffleId, user.uid);
        setHasReviewed(reviewed);
      } catch (err) {
        console.error('Error checking review status:', err);
        // On error (e.g., missing index), assume not reviewed so prompt still shows
        setHasReviewed(false);
      } finally {
        setLoading(false);
      }
    }

    checkReview();
  }, [raffleId, user?.uid, revieweeId]);

  // Submit a review
  const submitReview = useCallback(
    async (rating, text) => {
      if (!user?.uid || !revieweeId || !raffleId) {
        return false;
      }

      if (rating < 1 || rating > 5) {
        setError('Please select a rating between 1 and 5');
        return false;
      }

      setSubmitting(true);
      setError(null);

      try {
        await createReview({
          raffleId,
          reviewerId: user.uid,
          revieweeId,
          rating,
          text: text?.trim() || null,
        });

        // Update reviewee's average rating
        // This would ideally be done in a Cloud Function for accuracy
        // but for now we just increment their review count
        await updateDocument('users', revieweeId, {
          reviewCount: 1, // This should use increment() for accuracy
        });

        setHasReviewed(true);
        return true;
      } catch (err) {
        console.error('Error submitting review:', err);
        setError('Failed to submit review. Please try again.');
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [raffleId, user?.uid, revieweeId]
  );

  return {
    hasReviewed,
    loading,
    submitting,
    error,
    submitReview,
  };
}

export default useReview;
